import {Observable, Subject, Subscription} from 'rxjs';

import IErrorsPayload               from '../../Player/Interfaces/IErrorsPayload';
import Player                       from '../../Player/Player';
import getUserDevice                from '../../Shared/Util/getUserDevice';
import Media                        from '../../State/Stores/Media/Media';
import connectSolutionSubscriptions from '../Html5/connectSolutionSubscriptions';
import Html5Base                    from '../Shared/Abstract/Html5Base';
import SolutionType                 from '../Shared/Constants/SolutionType';
import SyntheticMediaEventType      from '../Shared/Constants/SyntheticMediaEventType';
import IMediaEventsStreamPayload    from '../Shared/Interfaces/IMediaEventsStreamPayload';
import ISolutionState               from '../Shared/Interfaces/ISolutionState';
import createMediaEventsStream      from '../Shared/Streams/createMediaEventsStream';
import createVideoElement           from '../Shared/Util/createVideoElement';
import stringToUint16Array          from '../Shared/Util/stringToUint16Array';

import getCurrentBitrateFromManifest from './Abr/getCurrentBitrateFromManifest';
import authenticateSession           from './Drm/authenticateSession';
import createKey                     from './Drm/createKey';
import createSession                 from './Drm/createSession';
import initEncryptedStream           from './Drm/initEncryptedStream';
import requestCertificate            from './Drm/requestCertifcate';
import createMediaErrorsStream       from './Streams/createMediaErrorsStream';

class Html5Fairplay extends Html5Base {
    public type: SolutionType;
    public version: string;
    public mediaEvents$: Observable<IMediaEventsStreamPayload>;
    public mediaErrors$: Observable<IErrorsPayload>;
    public videoElement: HTMLVideoElement;
    protected subscriptions: Subscription[];
    protected syntheticMediaEvents$: Subject<SyntheticMediaEventType>;
    private loadErrors$ = new Subject<Error>();
    private activeTextTrack: TextTrack;
    private player: Player;

    constructor(player: Player, type: SolutionType) {
        super();

        const {stores} = player.state;

        this.type = type;
        this.player = player;
        this.version = getUserDevice(navigator.userAgent).browserVersion;

        this.videoElement = createVideoElement(player.refs.solution, this.player.state.stores.volume.value.level);
        this.mediaErrors$ = createMediaErrorsStream(this.videoElement, this.loadErrors$, stores.playback.update$);
        this.mediaEvents$ = createMediaEventsStream(stores, this, this.syntheticMediaEvents$);
        this.subscriptions = connectSolutionSubscriptions(stores, this);
        this.handleCueChange = this.handleCueChange.bind(this);
    }

    public get state(): ISolutionState {
        const {currentTime, duration, volume, seekable, videoHeight, videoWidth} = this.videoElement;
        const seekableRangeCount = seekable.length;
        const activeCues = this.activeTextTrack && this.activeTextTrack.activeCues ?
            Array.from(this.activeTextTrack.activeCues).map(cue => ({text: cue.text})) :
            [];

        let rangeStart = 0;
        let rangeEnd = duration;

        if (seekableRangeCount > 0) {
            rangeStart = seekable.start(seekableRangeCount - 1);
            rangeEnd = seekable.end(seekableRangeCount - 1);
        }

        return {
            currentTime,
            minTime: rangeStart,
            maxTime: rangeEnd,
            resolution: `${videoWidth}x${videoHeight}`,
            volume,
            activeCues,
            estimatedBandwidth: -1
        };
    }

    public async load(media: Media): Promise<void> {
        const {src} = media;
        const {config} = this.player.state.stores;
        const {releasePid: contentId} = config.value.drm;

        try {
            if (!media.isEncrypted) {
                this.videoElement.src = src;

                return;
            }

            const [initData, certificate] = await Promise.all([
                initEncryptedStream(this.videoElement, src),
                requestCertificate(config.value)
            ]);

            const key = createKey(initData, stringToUint16Array(contentId), certificate);
            const session = createSession(this.videoElement, key, contentId);

            await authenticateSession(session, config.value);

            if (media.startTime > 0) this.seek(media.startTime);

            this.syntheticMediaEvents$.next(SyntheticMediaEventType.READY);
        } catch (err) {
            this.loadErrors$.next(err);
        }
    }

    public async getBitrate(): Promise<number> {
        const {media} = this.player.state.stores;
        const {src} = media.value;

        return await getCurrentBitrateFromManifest(src);
    }

    public setActiveTextTrack(index: number): void {
        const textTrack = this.videoElement.textTracks[index] || null;

        if (index > -1) {
            textTrack.mode = 'hidden';
            textTrack.addEventListener('cuechange', this.handleCueChange);
        } else if (textTrack) {
            textTrack.mode = 'disabled';
            textTrack.removeEventListener('cuechange', this.handleCueChange);
        }

        this.activeTextTrack = textTrack;
        this.handleCueChange();
    }

    private handleCueChange(): void {
        this.syntheticMediaEvents$.next(SyntheticMediaEventType.CUE_CHANGE);
    }
}

export default Html5Fairplay;